<script setup lang="ts">
import { onBeforeMount, ref, computed, toRefs } from 'vue';
import Chip from 'primevue/chip';
import CheckboxForm from '@/components/Forms/CheckboxForm';
import { PERMISSIONS } from '@/enum/permissions';
import { can } from '@/composables';
import { useDisciplineStore, useAuthStore } from '@/store';
import { DisciplineInterface } from '@/types';

interface Props {
  organizationId: number;
}

const permissionToUpdate = can([PERMISSIONS.ORGANIZATION_SYNC_DISCIPLINES]);

const props = defineProps<Props>();
const disciplineStore = useDisciplineStore();
const authStore = useAuthStore();
const { organizationId } = toRefs(props);

const dirty = ref(false);

// eslint-disable-next-line
// @ts-ignore
const activeDisciplines = computed<DisciplineInterface[]>(() =>
  disciplineStore.disciplines?.filter((discipline) => discipline.checked),
);

const onSave = async () => {
  const result = await disciplineStore.saveDisciplines(organizationId.value);

  result === 200
    ? disciplineStore.doToast('Disciplines have been saved', 'Saved')
    : disciplineStore.doToast('There was an error saving. Please try again', 'Error', true);
};

const onUpdate = (id: string, checked: boolean) => {
  if (!dirty.value) {
    dirty.value = true;
  }
  disciplineStore.updateDiscipline({ id, checked });
};

const onCancel = () => {
  dirty.value = false;
  disciplineStore.getDisciplines(organizationId.value);
};

const icons: Record<string, string> = {
  'Behavioral Intervention': 'emoji_objects',
  'Occupational Therapy': 'work',
  'Physical Therapy': 'emoji_people',
  'Social Work/Counseling': 'handshake',
  Psychology: 'psychology',
  'Speech And Language': 'record_voice_over',
  'Special Education Teachers': 'school',
  'Education Diagnosticians': 'assessment',
};

onBeforeMount(() => {
  if (can([PERMISSIONS.ORGANIZATION_DISCIPLINES_INDEX])) disciplineStore.getDisciplines(organizationId.value);
});
</script>

<template>
  <CheckboxForm
    v-if="!authStore.currentInstanceIsMasterTenant && permissionToUpdate"
    title="Disciplines"
    :items="disciplineStore.disciplines"
    :update-item="onUpdate"
    :save="onSave"
    :loading="disciplineStore.loading && permissionToUpdate"
    :cancel="onCancel"
    :dirty="dirty && permissionToUpdate"
    class-name="checkbox-form"
  />
  <Card v-else class="form-card p-fluid checkbox-form">
    <template #header>
      <h2>Disciplines</h2>
    </template>
    <template #content>
      <div class="flex flex-column">
        <div v-if="disciplineStore.loading">
          <Skeleton v-for="n in 5" :key="n" width="100%" height="24px" />
        </div>
        <div v-for="item of activeDisciplines" :key="item.id" class="mb-2">
          <Chip class="pl-0 pr-1">
            <Icon :name="icons[item.name] || 'category'" size="text-base" class="ml-1 text-color-purple" />
            <span class="p-chip-text ml-1 font-semibold" v-text="item.name" />
          </Chip>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.checkbox-form {
  height: 100%;
  :deep(.p-chip) {
    background-color: $grayLight;
    height: 22px;
    border-radius: 4px;
    .p-chip-text {
      font-size: 14px;
    }
  }
}
</style>
